/**
 * Always-visible client workspace identity + switcher (multi-client plan).
 * DXC home users (any role) — client-org users never see other clients'
 * existence.
 *
 * HUD callout sits in the left cluster as a committed true-blue identity
 * block (mark + CLIENT eyebrow + name), not a warning chip. Opening it
 * reveals a console-styled popover; confirming a switch writes the pick
 * to sessionStorage and reloads so ~30 Zustand stores / ETag maps /
 * TanStack cache reset by construction.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronsUpDown, Loader2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { isMultiClientEnabled, isZitadelMode } from "@/env";
import { useAuth } from "@/hooks/use-auth";
import {
  storeClientPick,
  useActiveClient,
} from "@/lib/active-client";
import { can } from "@/lib/auth/can";
import { listClients, type ClientEntry } from "@/lib/docs-api";
import { listActiveSeedClients } from "@/lib/seed-clients";
import { ClientMark } from "./ClientMark";

export function ClientBadge() {
  const { user } = useAuth();
  const { clientId, clientName, resolved } = useActiveClient();
  const [open, setOpen] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const enabled = isMultiClientEnabled() && can(user, "switch:client");

  const { data, isLoading, isError, refetch } = useQuery<ClientEntry[]>({
    queryKey: ["clients"],
    queryFn: listClients,
    enabled: enabled && open && isZitadelMode(),
  });

  if (!enabled || !resolved || !clientId) return null;

  const clients: ClientEntry[] = isZitadelMode()
    ? (data ?? []).filter((c) => c.status === "active")
    : listActiveSeedClients();
  const pending = clients.find((c) => c.id === pendingId) ?? null;
  const displayName = clientName ?? clientId;

  function confirmSwitch() {
    if (!pending) return;
    storeClientPick(pending.id);
    window.location.reload();
  }

  return (
    <>
      <div
        aria-hidden
        style={{
          width: 1,
          height: 28,
          background: "hsl(var(--border))",
          margin: "0 12px",
          flexShrink: 0,
        }}
      />
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setPendingId(null);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            title={`Client workspace: ${displayName}. Click to switch.`}
            aria-label={`Client workspace ${displayName}. Open switcher.`}
            data-testid="client-badge"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexShrink: 0,
              maxWidth: 240,
              minWidth: 0,
              padding: "3px 10px 3px 3px",
              borderRadius: 8,
              border: "1px solid rgba(var(--accent-true-blue-rgb), 0.5)",
              background: "rgba(var(--accent-true-blue-rgb), 0.12)",
              boxShadow: "0 0 18px rgba(var(--accent-true-blue-rgb), 0.18)",
              cursor: "pointer",
              transition: "border-color 0.2s, background 0.2s",
            }}
          >
          <ClientMark name={displayName} size="md" />
          {/* Label collapses to the mark alone on a phone-width HUD
              (`.hud-client-label` in index.css) so the DXC home button
              and the action buttons both keep their place. */}
          <span
            className="hud-client-label flex"
            style={{
              flexDirection: "column",
              gap: 1,
              minWidth: 0,
              textAlign: "left",
              flex: 1,
            }}
          >
            <span
              className="font-mono"
              style={{
                fontSize: "var(--fs-label)",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "var(--accent-true-blue)",
                lineHeight: 1.1,
              }}
            >
              Client
            </span>
            <span
              className="font-mono"
              style={{
                fontSize: "var(--fs-sub)",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--text-primary)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: 1.2,
              }}
            >
              {displayName}
            </span>
          </span>
          <ChevronsUpDown
            aria-hidden
            style={{
              width: 14,
              height: 14,
              flexShrink: 0,
              color: "var(--accent-true-blue)",
              opacity: 0.85,
            }}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[min(340px,calc(100vw-24px))] border-0 p-0 shadow-none"
        style={{ background: "transparent" }}
      >
        <div
          className="panel-glow-mc flex flex-col overflow-hidden"
          style={{
            background: "var(--bg-panel-alt)",
            borderRadius: 10,
          }}
        >
          <div
            className="flex flex-col gap-1 px-4 py-3"
            style={{ borderBottom: "1px solid hsl(var(--border))" }}
          >
            <p
              className="font-mono font-bold tracking-[0.14em] uppercase"
              style={{
                color: "var(--text-primary)",
                fontSize: "var(--fs-base)",
              }}
            >
              Switch client workspace
            </p>
            <p
              className="font-mono"
              style={{
                color: "var(--text-muted)",
                fontSize: "var(--fs-label)",
                letterSpacing: "0.04em",
                lineHeight: 1.45,
              }}
            >
              This tab only. Confirming reloads into their workspace; this
              client&apos;s local cache stays put.
            </p>
          </div>

          <ul
            className="flex max-h-64 flex-col gap-1.5 overflow-y-auto px-3 py-3"
            role="listbox"
            aria-label="Client workspaces"
          >
            {isZitadelMode() && isLoading && (
              <li
                className="flex items-center gap-2 px-1 py-2 font-mono"
                style={{
                  color: "var(--text-muted)",
                  fontSize: "var(--fs-label)",
                }}
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Loading clients…
              </li>
            )}
            {isZitadelMode() && isError && (
              <li className="px-1 py-2 font-mono" style={{ fontSize: "var(--fs-label)" }}>
                <p style={{ color: "var(--accent-red, #c44)" }}>
                  Couldn&apos;t load the client registry.
                </p>
                <button
                  type="button"
                  onClick={() => void refetch()}
                  className="mt-2 rounded border px-2.5 py-1 font-mono uppercase tracking-widest"
                  style={{
                    borderColor: "hsl(var(--border))",
                    color: "var(--text-secondary)",
                    fontSize: "var(--fs-label)",
                  }}
                >
                  Retry
                </button>
              </li>
            )}
            {!isLoading &&
              !isError &&
              clients.map((client) => {
                const isCurrent = client.id === clientId;
                const isPending = client.id === pendingId;
                return (
                  <li key={client.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isPending}
                      disabled={isCurrent}
                      onClick={() => setPendingId(client.id)}
                      className="flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left transition-colors"
                      style={{
                        border: isPending
                          ? "1px solid rgba(var(--accent-true-blue-rgb), 0.55)"
                          : "1px solid hsl(var(--border))",
                        background: isPending
                          ? "rgba(var(--accent-true-blue-rgb), 0.1)"
                          : "transparent",
                        opacity: isCurrent ? 0.55 : 1,
                        cursor: isCurrent ? "default" : "pointer",
                      }}
                    >
                      <ClientMark
                        name={client.name}
                        logoUrl={client.logoUrl}
                        size="sm"
                      />
                      <span className="min-w-0 flex-1">
                        <span
                          className="block truncate font-mono font-semibold uppercase tracking-wide"
                          style={{
                            color: "var(--text-primary)",
                            fontSize: "var(--fs-base)",
                          }}
                        >
                          {client.name}
                        </span>
                        <span
                          className="block truncate font-mono"
                          style={{
                            color: "var(--text-muted)",
                            fontSize: "var(--fs-label)",
                            letterSpacing: "0.08em",
                          }}
                        >
                          {isCurrent ? "Current workspace" : client.id}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            {!isLoading && !isError && clients.length === 0 && (
              <li
                className="px-1 py-2 font-mono"
                style={{
                  color: "var(--text-muted)",
                  fontSize: "var(--fs-label)",
                }}
              >
                No active clients. Add one under Manage Crew, then reload.
              </li>
            )}
          </ul>

          <div
            className="flex items-center justify-end gap-2 px-3 py-3"
            style={{ borderTop: "1px solid hsl(var(--border))" }}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded border px-3 py-1.5 font-mono uppercase tracking-[0.18em]"
              style={{
                borderColor: "hsl(var(--border))",
                color: "var(--text-secondary)",
                fontSize: "var(--fs-label)",
                background: "transparent",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!pending}
              onClick={confirmSwitch}
              className="rounded border px-3 py-1.5 font-mono font-semibold uppercase tracking-[0.18em] disabled:pointer-events-none disabled:opacity-40"
              style={{
                borderColor: "var(--accent-true-blue)",
                color: "var(--accent-true-blue)",
                background: "rgba(var(--accent-true-blue-rgb), 0.14)",
                fontSize: "var(--fs-label)",
              }}
            >
              {pending ? `Switch to ${pending.name}` : "Switch"}
            </button>
          </div>
        </div>
      </PopoverContent>
      </Popover>
    </>
  );
}
